import dayjs from "dayjs";
import { cloneDeep, get, isEmpty, isEqual } from "lodash-es";
import { defineStore } from "pinia";
import { useRoute } from "vue-router";

import {
  getHotelRatesAsync,
  getHotels,
  getHotelsAsync
} from "@/api/biz/openHotel";
import {
  clearUserRecentHistory,
  getUserRecentHistory,
  setUserRecentHistory
} from "@/api/biz/user";
import { useToggles } from "@/hooks/useToggles";
import { store } from "@/store";
import { useChannelStoreHook } from "@/store/modules/channel";
import { useUserStoreHook } from "@/store/modules/user";
import {
  addLocalHistoryItem,
  clearLocalHistory,
  getLocalHistory
} from "@/utils/history";
import { queryToParams } from "@/utils/tool";
const userStore = useUserStoreHook();
const channelStore = useChannelStoreHook();

export const useHotelStore = defineStore({
  id: "y-hotel",
  state: () => ({
    // 酒店/房型搜索公用参数参数
    searchModel: {
      preferred: {
        currency: channelStore.channel?.currency,
        language: userStore.languageISO639Style,
        version: "v1"
      },
      stay: {
        date: {
          checkIn: dayjs().format("YYYY-MM-DD"),
          checkOut: dayjs().add(1, "day").format("YYYY-MM-DD")
        },
        guest: {
          numberOfRooms: 1,
          numberOfAdults: 2,
          numberOfChildren: 0
        }
      }
    },
    // 酒店搜索参数
    hotelSearchModel: {
      filter: {},
      sort: "byRecommendation",
      location: {},
      commissionPromotion: "", // 是否筛选高佣
      locationRef: "", // locationRef
      locationType: "city", // location类型 city/modelTpHotel/googleMapPolitical
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        total: 0
      }
    },
    /** 选中的酒店 */
    selectedHotel: null,
    /** 选中的房间 */
    selectedRoom: null,
    /** 选中的rate */
    selectedRate: null,

    /**分割线 上面是搜索参数相关，下面是数据展示 */

    /** 酒店列表 */
    hotelList: [],
    /** 历史记录 */
    historyList: [],
    /** 酒店房型列表 */
    roomList: [],
    /** 酒店详情的状态 */
    hotelDetail: {
      isLoading: true,
      isLoaded: false
    },
    hotelInitLoading: true, // 酒店列表初始化的loading
    hotelListInitialLoadDone: false,
    roomListInitLoading: false, // 房型列表初始化的loading
    roomListInitLoadingDone: false, //房型列表初始化的loading是否完成
    hotelListLoadingMore: false, // 酒店列表加载更多loading
    roomListLoadingMore: false, // 房型列表加载更多loading
    hotelListNoMore: false, // 酒店列表没有更多了
    extraBrands: [], // 额外的品牌
    showCommission: true // 是否显示佣金
  }),
  actions: {
    SET_SEARCH_MODEL(model) {
      this.searchModel = { ...this.searchModel, ...model };
    },
    SET_HOTEL_SEARCH_MODEL(model) {
      this.hotelSearchModel = { ...this.hotelSearchModel, ...model };
    },
    SET_SELECTED_HOTEL(hotel) {
      this.selectedHotel = hotel;
    },
    SET_SELECTED_ROOM(room) {
      this.selectedRoom = room;
    },
    SET_SELECTED_RATE(rate) {
      this.selectedRate = rate;
    },
    setRatePriceDetail(priceDetail) {
      if (this.selectedRate) {
        this.selectedRate.priceDetail = priceDetail;
      } else {
        console.warn("没有房型，无法设置价格");
      }
    },
    initialize(options = {}) {
      const { query = {}, force = false } = options;

      if (force || !this.searchModel.preferred.currency) {
        this.searchModel.preferred.currency = channelStore.channel?.currency;
      }

      const stayUpdate = {};
      if (query.checkIn) stayUpdate.checkIn = query.checkIn;
      if (query.checkOut) stayUpdate.checkOut = query.checkOut;

      const guestUpdate = {};
      if (query.numberOfRooms)
        guestUpdate.numberOfRooms = Number(query.numberOfRooms);
      if (query.numberOfAdults)
        guestUpdate.numberOfAdults = Number(query.numberOfAdults);

      if (Object.keys(stayUpdate).length > 0) {
        this.searchModel.stay.date = {
          ...this.searchModel.stay.date,
          ...stayUpdate
        };
      }
      if (Object.keys(guestUpdate).length > 0) {
        this.searchModel.stay.guest = {
          ...this.searchModel.stay.guest,
          ...guestUpdate
        };
      }
      this.resetPagination();
      if (force) {
        this.hotelList = [];
        this.hotelListNoMore = false;
        this.hotelListInitialLoadDone = false;
      }
    },
    resetPagination() {
      this.hotelSearchModel.pagination = {
        pageIndex: 1,
        pageSize: 10,
        total: 0
      };
    },
    updatePagination(totalCount) {
      this.hotelSearchModel.pagination.pageIndex += 1;
      this.hotelSearchModel.pagination.total = totalCount;
    },
    async initHotelList() {
      if (this.hotelListLoadingMore) return;

      if (this.hotelSearchModel.pagination.pageIndex === 1) {
        this.hotelList = [];
        this.hotelListNoMore = false;
        this.hotelInitLoading = true;
        this.hotelListInitialLoadDone = false;
      }

      if (
        this.hotelSearchModel.pagination.total > 0 &&
        this.hotelList.length >= this.hotelSearchModel.pagination.total
      ) {
        this.hotelListNoMore = true;
        this.hotelInitLoading = false;
        this.hotelListLoadingMore = false;
        return;
      }

      if (this.hotelList.length > 0) {
        this.hotelListLoadingMore = true;
      }

      const params = {
        preferred: {
          ...this.searchModel.preferred,
          language: userStore.languageISO639Style
        },
        stay: this.searchModel.stay,
        filter: this.hotelSearchModel.filter,
        paging: {
          limit: this.hotelSearchModel.pagination.pageSize,
          skip:
            (this.hotelSearchModel.pagination.pageIndex - 1) *
            this.hotelSearchModel.pagination.pageSize
        },
        sort: this.hotelSearchModel.sort,
        options: {
          extraBrandsRequired: true
        }
      };

      const _queryParams = queryToParams();
      params.options = { ...params.options, ..._queryParams.options };
      if (Object.keys(params.filter).length === 0) {
        params.filter = _queryParams.filter;
      }
      if (this.hotelSearchModel.commissionPromotion === "highCommission") {
        params.filter._biz = { commissionPromotion: true };
      } else if (
        params.filter._biz &&
        Object.keys(params.filter._biz).length === 0
      ) {
        delete params.filter._biz;
      }
      if (params.filter._biz?.googleMapPolitical) {
        delete params.filter.search;
      }

      const brandParams = cloneDeep(params);
      delete brandParams.filter.brands;

      getHotelsAsync(brandParams, res => {
        this.extraBrands = get(res, "extra.brands", this.extraBrands);
      });

      getHotelsAsync(
        params,
        res => {
          const hotelsData = res.hotels || [];

          // 如果是最终返回且没有数据，直接处理
          if (hotelsData.length === 0 && !res.asyncJobId) {
            this.hotelInitLoading = false;
            this.hotelListLoadingMore = false;
            this.hotelSearchModel.pagination.total = this.hotelList.length;
            this.hotelListNoMore = true;
            return;
          }

          const incomingIds = hotelsData.map(h => h._id);
          const allExist = incomingIds.every(id =>
            this.hotelList.some(h => h._id === id)
          );
          const isUpdateOperation =
            this.hotelList.length > 0 && hotelsData.length > 0 && allExist;

          if (isUpdateOperation) {
            hotelsData.forEach(asyncItem => {
              const index = this.hotelList.findIndex(
                h => h._id === asyncItem._id
              );
              if (index !== -1) {
                this.hotelList[index] = {
                  ...this.hotelList[index],
                  ...asyncItem
                };
              }
            });
          } else {
            const uniqueHotels = hotelsData.filter(
              newItem => !this.hotelList.some(h => h._id === newItem._id)
            );
            this.hotelList = [...this.hotelList, ...uniqueHotels];

            this.updatePagination(res.paging?.count || this.hotelList.length);
          }

          this.hotelInitLoading = false;
          this.hotelListLoadingMore = false;

          const total = this.hotelSearchModel.pagination.total;
          this.hotelListNoMore = this.hotelList.length >= total;

          if (!this.hotelListInitialLoadDone) {
            this.hotelListInitialLoadDone = true;
          }
        },
        () => {
          // 错误处理
          this.hotelInitLoading = false;
          this.hotelListLoadingMore = false;
          console.error("getHotelsAsync 出错");
          if (!this.hotelListInitialLoadDone) {
            this.hotelListInitialLoadDone = true;
          }
        }
      );
    },

    async initHotelDetail() {
      const userStore = useUserStoreHook();
      this.roomList = [];
      this.roomListInitLoading = false;
      this.roomListInitLoadingDone = false;
      this.hotelDetail.isLoading = true;
      this.hotelDetail.isLoaded = false;
      const route = useRoute();
      let _hotelId = route?.query?.hotelId;
      if (!_hotelId && !isEmpty(this.selectedHotel)) {
        _hotelId = this.selectedHotel?._id;
      }
      this.selectedHotel = null;
      const params = {
        preferred: {
          ...this.searchModel.preferred,
          language: userStore.languageISO639Style
        },
        stay: this.searchModel.stay,
        filter: {
          hotels: [{ _id: _hotelId }]
        },
        paging: { limit: 1, skip: 0 }
      };

      try {
        const res = await getHotels(params);
        if (res.success && res.hotels) {
          this.selectedHotel = res.hotels[0];
          this.hotelDetail.isLoaded = true;
          this.hotelDetail.isLoading = false;
        }
      } catch (error) {
        console.error("initHotelDetail 出错", error);
      } finally {
        this.hotelDetail.isLoading = false;
      }
      this.initRoomList();
    },

    async initRoomList() {
      if (this.roomListInitLoading) return;
      this.roomListInitLoading = true;
      this.roomListInitLoadingDone = false;
      this.roomList = [];
      const userStore = useUserStoreHook();
      const route = useRoute();
      let _hotelId = route?.query?.hotelId;
      if (!_hotelId && !isEmpty(this.selectedHotel)) {
        _hotelId = this.selectedHotel?._id;
      }

      if (!_hotelId) {
        this.roomListInitLoading = false;
        this.roomListInitLoadingDone = true;
        return;
      }

      const params = {
        preferred: {
          ...this.searchModel.preferred,
          language: userStore.languageISO639Style
        },
        stay: this.searchModel.stay,
        hotel: { _id: _hotelId }
      };

      getHotelRatesAsync(
        params,
        res => {
          const newRooms = res.rooms || [];
          let changed = false;

          const newList = newRooms.map(newItem => {
            const oldItem = this.roomList.find(r => r._id === newItem._id);

            if (!oldItem) {
              changed = true;
              return { ...newItem, __isNew: true };
            }

            if (!isEqual(oldItem, newItem)) {
              changed = true;
              return { ...newItem, __isUpdated: true };
            }

            return { ...oldItem, __isSame: true };
          });

          if (changed) {
            this.roomList = newList;
          }

          if (!this.roomListInitLoadingDone) {
            this.roomListInitLoading = false;
            this.roomListInitLoadingDone = true;
          }
        },
        () => {
          this.roomListInitLoading = false;
          this.roomListInitLoadingDone = true;
        }
      );
    },

    initHotelQuery(query) {
      if (query.checkIn) {
        this.searchModel.stay.date.checkIn = query.checkIn;
      }
      if (query.checkOut) {
        this.searchModel.stay.date.checkOut = query.checkOut;
      }
      if (query.numberOfRooms) {
        this.searchModel.stay.guest.numberOfRooms = Number(query.numberOfRooms);
      }
      if (query.numberOfAdults) {
        this.searchModel.stay.guest.numberOfAdults = Number(
          query.numberOfAdults
        );
      }

      this.resetPagination();
    },
    async getSearchHistory() {
      const historyToggle = useToggles("Y_WEB_SEARCH_HISTORY");
      if (!historyToggle.value) {
        this.historyList = [];
        return;
      }

      const { user } = useUserStoreHook();
      if (user) {
        try {
          const response = await getUserRecentHistory({
            type: "searchUniversal"
          });
          this.historyList = response?.results || [];
        } catch (error) {
          this.historyList = [];
        }
      } else {
        this.historyList = getLocalHistory();
      }
    },
    async setSearchHistory(payload) {
      const { user } = useUserStoreHook();
      if (user) {
        try {
          await setUserRecentHistory(payload);
          this.getSearchHistory();
        } catch (error) {
          console.warn("记录【API】搜索历史失败:", error);
        }
      } else {
        addLocalHistoryItem(payload);
        this.historyList = getLocalHistory();
      }
    },
    async deleteHistory() {
      const { user } = useUserStoreHook();
      if (user) {
        try {
          await clearUserRecentHistory({
            type: "searchUniversal"
          });
          this.historyList = [];
        } catch (error) {
          console.warn("记录【API】搜索历史失败:", error);
        }
      } else {
        clearLocalHistory();
        this.historyList = [];
      }
    }
  },
  getters: {
    preferred() {
      return {
        currency: channelStore.channel?.currency,
        language: userStore.languageISO639Style,
        version: "v1"
      };
    }
  }
});

export function useHotelStoreHook() {
  return useHotelStore(store);
}
